import { Button, Card, Collapse, Form, Input, Select } from "antd";
import { getDeliveries, getDomain } from "api";
import DashboardHeader from "components/dashboard-header/DashboardHeader";
import DatePicker from "components/datepicker/DatePicker";
import { EmptyWrapper, LoadFailed } from "components/empty/EmptyComponent";
import { ICCloseOutlined, ICSearchOutlined, ICUpOutlined } from "components/list/ListIcon";
import Spinner from "components/Spinner/Spinner";
import TableDeliveries from "components/table/TableDeliveries";
import TableTitle from "components/tableSendiri/TableTitle";
import { endOfDay, format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setDeliveries } from "redux/slices/deliveries";
import { setListDomain } from "redux/slices/listDomain";
import useCursorPagination from "utils/hooks/useCursorPagination";
import useDocumentTitle from "utils/useDocumentTitle";
import "./deliveries.less";

const { Option } = Select;
const { Panel } = Collapse;

const ExtraHeader = ({ setFilter, filter }) => {
  const dispatch = useDispatch();
  const listDomain = useSelector(({ listDomain }) => listDomain.value);

  const getDataDomain = useCallback(async () => {
    const { data } = await getDomain();
    dispatch(setListDomain(data));
  }, [dispatch]);

  useEffect(() => {
    if (!listDomain) getDataDomain();
  }, [getDataDomain, listDomain]);

  const handleChangeDomain = async (domain) => {
    setFilter({ ...filter, domain, page: 1 });
  };

  const handleChangeStatus = async (status) => {
    setFilter({ ...filter, status, page: 1 });
  };

  return (
    <div className="flex justify-between items-center w-full">
      <span className="title-log-email">Filter</span>
      <div className="flex gap-x-6 items-center">
        <div className="flex gap-x-1 items-center">
          <span className="hidden-xs">Domain:</span>
          <Select
            name="domain"
            defaultValue="Semua Domain"
            onChange={handleChangeDomain}
            className="select-domain">
            <Option value="">Semua Domain</Option>
            {listDomain &&
              listDomain.map((domain) => (
                <Option value={domain.domainName} key={domain.domainUuid}>
                  {domain.domainName}
                </Option>
              ))}
          </Select>
        </div>
        <div className="flex gap-x-1 items-center">
          <span className="hidden-xs">Status:</span>
          <Select
            defaultValue="Semua Status"
            onChange={handleChangeStatus}
            className="select-status">
            <Option value="">Semua Status</Option>
            <Option value="in_queue">In Queue</Option>
            <Option value="deferred">Deferred</Option>
            <Option value="sent">Sent</Option>
            <Option value="bounced">Bounced</Option>
            <Option value="expired">Expired</Option>
          </Select>
        </div>
      </div>
    </div>
  );
};

const HeaderPanel = () => {
  const [click, setClick] = useState(false);

  return (
    <div className="flex items-center w-full">
      <span className="text-success" onClick={() => setClick((prev) => !prev)}>
        Advanced Filter
      </span>
      <ICUpOutlined className="ml-1 text-success" rotate={click ? 0 : 180} />
    </div>
  );
};

const Deliveries = () => {
  let dataCSV = [];
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(false);
  const [endDate, setEndDate] = useState(false);
  const [filter, setFilter] = useState({
    domain: "",
    status: "",
    sender: "",
    recipient: "",
    page: 1,
    limit: "10",
  });

  useDocumentTitle("Log Email v2");
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const deliveries = useSelector(({ deliveries }) => deliveries.value);

  // Cursor pagination resets whenever the active filter set changes, or when the
  // user hits Refresh (bumping refreshTick) — "load the latest" from page 1.
  const [refreshTick, setRefreshTick] = useState(0);
  const { cursor, pageIndex, canPrev, goNext, goPrev } = useCursorPagination(
    `${JSON.stringify(filter)}#${refreshTick}`
  );

  const resetfilter = () => {
    form.resetFields();
    let newFilter = { ...filter };
    delete newFilter.datetime;
    delete newFilter.to_datetime;
    let filtered = {
      ...newFilter,
      page: 1,
      sender: "",
      recipient: "",
    };
    setFilter(filtered);
  };

  const hitApi = useCallback(async () => {
    setLoading(true);
    const { data, message, success } = await getDeliveries({ ...filter, cursor });
    dispatch(setDeliveries({ tableDeliveriesUser: data, message, success }));
    setLoading(false);
  }, [filter, cursor, dispatch]);

  useEffect(() => {
    if (filter) hitApi();
    // refreshTick is a manual-refresh trigger: it re-runs the fetch even when the
    // filter/cursor are unchanged (e.g. hitting Refresh while already on page 1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hitApi, filter, refreshTick]);

  const handleRefresh = () => setRefreshTick((tick) => tick + 1);

  const onChangeDatetime = (values) => {
    if (values && values.length > 0) {
      const startDate = format(new Date(values[0]), "yyyy-MM-dd");
      const endDate = format(new Date(values[1]), "yyyy-MM-dd");
      setStartDate(`${startDate} 00:00:00`);
      setEndDate(`${endDate} 23:59:59`);
    } else {
      setStartDate(false);
      setEndDate(false);
    }
  };

  const handleAdvanceFilter = async (values) => {
    const { sender, recipient } = values;
    delete filter.datetime;
    delete filter.to_datetime;
    let filtered = { ...filter, page: 1, sender, recipient };
    if (startDate && endDate)
      filtered = {
        ...filter,
        page: 1,
        sender,
        recipient,
        datetime: startDate,
        to_datetime: endDate,
      };
    setFilter(filtered);
  };

  const titleMessage = deliveries.success ? "Data tidak ditemukan" : "Data belum tersedia";
  const message = deliveries.success
    ? "Data pengiriman yang anda cari tidak ditemukan, pastikan tidak ada salah ketik pada penulisan anda"
    : `Data pengiriman akan muncul setelah client melakukan pengiriman email menggunakan akun ${process.env.REACT_APP_NAME}.`;

  const disabledDate = (current) => {
    const lastMonth = new Date();
    lastMonth.setDate(lastMonth.getDate() - 31);
    return current && (current < lastMonth || current > endOfDay(new Date()));
  };

  return (
    <div className="log-email-user">
      <DashboardHeader title="Log Email Terkirim (v2)" />
      <div className="log-email-user px-6 py-4">
        <Collapse defaultActiveKey={["0"]} ghost className="mb-6 log-collapse">
          <Panel
            header={<HeaderPanel />}
            key="1"
            extra={<ExtraHeader filter={filter} setFilter={setFilter} rotate={false} />}
            showArrow={false}
            collapsible="header"
            className="log-email-header">
            <Form
              layout="inline"
              className="formFilter"
              form={form}
              onFinish={handleAdvanceFilter}
              initialValues={{
                sender: "",
                recipient: "",
                datetime: "",
              }}>
              <div>
                <Form.Item label="Sender" name="sender">
                  <Input type="text" placeholder="sender@domain.tld" />
                </Form.Item>
                <Form.Item label="Recipient" name="recipient">
                  <Input type="text" placeholder="recipient@domain.tld" />
                </Form.Item>
                <Form.Item name="datetime">
                  <DatePicker.RangePicker
                    className="w-full"
                    disabledDate={disabledDate}
                    onChange={onChangeDatetime}
                    format="yyyy-MM-dd"
                  />
                </Form.Item>
              </div>
              <div className="button-advance-filter">
                <Button onClick={resetfilter} icon={<ICCloseOutlined />}>
                  Reset
                </Button>
                <Button type="primary" icon={<ICSearchOutlined />} htmlType="submit">
                  Cari
                </Button>
              </div>
            </Form>
          </Panel>
        </Collapse>
        {deliveries.tableDeliveriesUser?.count > 0 ? (
          <TableDeliveries
            filter={filter}
            setFilter={setFilter}
            loading={loading}
            hitApi={hitApi}
            pageIndex={pageIndex}
            canPrev={canPrev}
            onPrev={goPrev}
            onNext={goNext}
            onRefresh={handleRefresh}
          />
        ) : deliveries ? (
          <div className="site-card-border-less-wrapper">
            <Card
              loading={loading}
              bordered={false}
              title={
                <TableTitle
                  title="Deliveries"
                  desc={`Log pengiriman email (v2) ${process.env.REACT_APP_NAME}. Klik untuk melihat detail`}
                  buttonRefresh={{
                    onClick: () => {
                      hitApi();
                    },
                  }}
                  loading={loading}
                  buttonExport={{ disable: true, dataCSV: dataCSV, responsive: "lg" }}
                  pageLimit={{
                    currentLimit: filter.limit,
                    disable: true,
                    onSelectLimit: () => {},
                  }}
                />
              }>
              {deliveries.success === false ? (
                <LoadFailed />
              ) : (
                <EmptyWrapper imgMargin="mb-4" title={titleMessage} message={message} />
              )}
            </Card>
          </div>
        ) : (
          <Spinner loading={loading} full />
        )}
      </div>
    </div>
  );
};

export default Deliveries;
